import { supabase } from './supabase';
import type { RegistryStats, AdminWithdrawalRequest, AdminDashboardStats, UserStats, AdminVendor, AdminVenue, VendorFormData, VenueFormData } from '../types/admin';

export async function isAdmin(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_admin');
    if (error) throw error;
    return data || false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

export async function getRegistryStats(): Promise<RegistryStats[]> {
  try {
    const { data, error } = await supabase.rpc('get_admin_registry_stats');
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching registry stats:', error);
    throw error;
  }
}

export async function getUserStats(): Promise<UserStats[]> {
  try {
    const { data, error } = await supabase.rpc('get_admin_user_stats');
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user stats:', error);
    throw error;
  }
}

export async function getAdminWithdrawalRequests(): Promise<AdminWithdrawalRequest[]> {
  try {
    const { data, error } = await supabase.rpc('get_admin_withdrawal_requests');
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching withdrawal requests:', error);
    throw error;
  }
}

export async function approveWithdrawalRequest(requestId: string, adminNotes?: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('withdrawal_requests')
      .update({
        status: 'approved',
        approved_at: new Date().toISOString(),
        admin_notes: adminNotes
      })
      .eq('id', requestId);

    if (error) throw error;

    // Log admin action
    await supabase.rpc('log_admin_action', {
      action_type_param: 'approve_withdrawal',
      target_type_param: 'withdrawal_request',
      target_id_param: requestId,
      new_values_param: { status: 'approved', admin_notes: adminNotes }
    });
  } catch (error) {
    console.error('Error approving withdrawal request:', error);
    throw error;
  }
}

export async function markAsTransferred(
  requestId: string,
  transferReference: string,
  adminNotes?: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('withdrawal_requests')
      .update({
        status: 'transferred',
        transferred_at: new Date().toISOString(),
        transfer_reference: transferReference,
        admin_notes: adminNotes
      })
      .eq('id', requestId);

    if (error) throw error;

    // Log admin action
    await supabase.rpc('log_admin_action', {
      action_type_param: 'mark_transferred',
      target_type_param: 'withdrawal_request',
      target_id_param: requestId,
      new_values_param: { 
        status: 'transferred', 
        transfer_reference: transferReference,
        admin_notes: adminNotes 
      }
    });
  } catch (error) {
    console.error('Error marking as transferred:', error);
    throw error;
  }
}

export async function deleteRegistry(registryId: string, reason?: string): Promise<void> {
  try {
    // First, get registry details for logging
    const { data: registry, error: fetchError } = await supabase
      .from('registries')
      .select('title, user_id')
      .eq('id', registryId)
      .single();

    if (fetchError) throw fetchError;

    // Delete the registry (cascade will handle related records)
    const { error } = await supabase
      .from('registries')
      .delete()
      .eq('id', registryId);

    if (error) throw error;

    // Log admin action
    await supabase.rpc('log_admin_action', {
      action_type_param: 'delete_registry',
      target_type_param: 'registry',
      target_id_param: registryId,
      old_values_param: { 
        title: registry.title, 
        user_id: registry.user_id 
      },
      new_values_param: { 
        deleted: true, 
        reason: reason || 'No reason provided' 
      }
    });
  } catch (error) {
    console.error('Error deleting registry:', error);
    throw error;
  }
}

export async function deleteUser(userId: string, reason?: string): Promise<void> {
  try {
    const { error } = await supabase.rpc('delete_user_and_data', {
      target_user_id: userId,
      deletion_reason: reason
    });

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
}

export async function getDashboardStats(): Promise<AdminDashboardStats> {
  try {
    const [registryStats, withdrawalRequests] = await Promise.all([
      getRegistryStats(),
      getAdminWithdrawalRequests()
    ]);

    const totalRegistries = registryStats.length;
    const totalCollected = registryStats.reduce((sum, reg) => sum + reg.total_collected, 0);
    const totalWithdrawn = registryStats.reduce((sum, reg) => sum + reg.total_withdrawn, 0);
    
    const pendingWithdrawals = withdrawalRequests.filter(req => req.status === 'pending');
    const pendingWithdrawalAmount = pendingWithdrawals.reduce((sum, req) => sum + req.amount, 0);

    // Get unique active users count
    const activeUsers = new Set(registryStats.map(reg => reg.owner_email)).size;

    return {
      total_registries: totalRegistries,
      total_collected: totalCollected,
      pending_withdrawals: pendingWithdrawals.length,
      pending_withdrawal_amount: pendingWithdrawalAmount,
      total_withdrawn: totalWithdrawn,
      active_users: activeUsers
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
}

export async function exportRegistryData(): Promise<void> {
  try {
    const data = await getRegistryStats();
    
    const csvContent = [
      ['Registry ID', 'Owner Name', 'Owner Email', 'Contact', 'Title', 'Created Date', 'Total Collected', 'Pending Requests', 'Total Withdrawn'].join(','),
      ...data.map(row => [
        row.registry_id,
        `"${row.owner_name}"`,
        row.owner_email,
        `"${row.contact_number || ''}"`,
        `"${row.title}"`,
        new Date(row.created_at).toLocaleDateString(),
        row.total_collected,
        row.pending_requests_count,
        row.total_withdrawn
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `registry_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting data:', error);
    throw error;
  }
}

export async function exportWithdrawalData(): Promise<void> {
  try {
    const data = await getAdminWithdrawalRequests();
    
    const csvContent = [
      ['Request ID', 'Registry Title', 'Owner Name', 'Owner Email', 'Amount', 'Status', 'Requested Date', 'Transfer Reference', 'Admin Notes'].join(','),
      ...data.map(row => [
        row.request_id,
        `"${row.registry_title}"`,
        `"${row.owner_name}"`,
        row.owner_email,
        row.amount,
        row.status,
        new Date(row.requested_at).toLocaleDateString(),
        `"${row.transfer_reference || ''}"`,
        `"${row.admin_notes || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `withdrawal_requests_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting withdrawal data:', error);
    throw error;
  }
}

export async function exportUserData(): Promise<void> {
  try {
    const data = await getUserStats();
    
    const csvContent = [
      ['User ID', 'Email', 'Full Name', 'Username', 'Created Date', 'Last Sign In', 'Registry Count', 'Total Collected', 'Total Withdrawn', 'Pending Requests'].join(','),
      ...data.map(row => [
        row.user_id,
        row.email,
        `"${row.full_name}"`,
        `"${row.username}"`,
        new Date(row.created_at).toLocaleDateString(),
        row.last_sign_in_at ? new Date(row.last_sign_in_at).toLocaleDateString() : 'Never',
        row.registry_count,
        row.total_collected,
        row.total_withdrawn,
        row.pending_requests_count
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `user_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting user data:', error);
    throw error;
  }
}

export async function updateFeeSettings(platformFeeRate: number, gatewayFeeRate: number): Promise<void> {
  try {
    console.log('updateFeeSettings - Starting update with values:', {
      platformFeeRate,
      gatewayFeeRate
    });

    // Update platform fee
    const { error: platformError } = await supabase 
      .from('system_settings')
      .update({ value: platformFeeRate.toString() })
      .eq('key', 'platform_fee_rate');

    if (platformError) {
      console.error('updateFeeSettings - Platform fee update error:', platformError);
      throw platformError;
    }
    console.log('updateFeeSettings - Platform fee updated successfully to:', platformFeeRate);

    // Update gateway fee
    const { error: gatewayError } = await supabase 
      .from('system_settings')
      .update({ value: gatewayFeeRate.toString() })
      .eq('key', 'gateway_fee_rate');

    if (gatewayError) {
      console.error('updateFeeSettings - Gateway fee update error:', gatewayError);
      throw gatewayError;
    }
    console.log('updateFeeSettings - Gateway fee updated successfully to:', gatewayFeeRate);
    
    // Call Edge Function to update all registries (bypasses RLS)
    console.log('updateFeeSettings - Calling Edge Function to update all registries...');
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-registry-fees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({ platformFeeRate, gatewayFeeRate })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('updateFeeSettings - Edge Function error response:', errorData);
      throw new Error(`Failed to update registries via Edge Function: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log('updateFeeSettings - Edge Function response:', result);
    
    console.log('updateFeeSettings - Successfully updated registries via Edge Function:', {
      count: result.updatedCount,
      platformFeeRate,
      gatewayFeeRate,
      registries: result.updatedRegistries
    });
    
    console.log('updateFeeSettings - All updates completed successfully');
  } catch (error) {
    console.error('Error updating fee settings:', error);
    throw error;
  }
}

export async function getFeeSettings(): Promise<{ platformFeeRate: number; gatewayFeeRate: number }> {
  try {
    // Get platform fee
    const { data: platformData, error: platformError } = await supabase
      .from('system_settings')
      .select('value')
      .eq('key', 'platform_fee_rate')
      .single();

    if (platformError) throw platformError;

    // Get gateway fee
    const { data: gatewayData, error: gatewayError } = await supabase
      .from('system_settings')
      .select('value')
      .eq('key', 'gateway_fee_rate')
      .single();

    if (gatewayError) throw gatewayError;

    const platformValue = parseFloat(platformData.value);
    const gatewayValue = parseFloat(gatewayData.value);
    
    console.log('getFeeSettings - Raw values from DB:', {
      platformData: platformData.value,
      gatewayData: gatewayData.value,
      platformValue,
      gatewayValue
    });

    return {
      platformFeeRate: platformValue,
      gatewayFeeRate: gatewayValue
    };
  } catch (error) {
    console.error('Error getting fee settings:', error);
    throw error;
  }
}
export async function resetUserPassword(userId: string, reason?: string): Promise<{ email: string; newPassword: string }> {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-user-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({ userId, reason })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to reset user password');
    }

    const result = await response.json();
    console.log('Password reset successful:', { email: result.email, passwordGenerated: true });
    
    return {
      email: result.email,
      newPassword: result.newPassword
    };
  } catch (error) {
    console.error('Error resetting user password:', error);
    throw error;
  }
}

// Vendor Management Functions
export async function getAdminVendors(): Promise<AdminVendor[]> {
  try {
    const { data, error } = await supabase
      .from('admin_vendors')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching admin vendors:', error);
    throw error;
  }
}

export async function createAdminVendor(vendorData: VendorFormData): Promise<AdminVendor> {
  try {
    const { data, error } = await supabase
      .from('admin_vendors')
      .insert([vendorData])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating admin vendor:', error);
    throw error;
  }
}

export async function updateAdminVendor(vendorId: string, updates: Partial<VendorFormData>): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_vendors')
      .update(updates)
      .eq('id', vendorId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating admin vendor:', error);
    throw error;
  }
}

export async function deleteAdminVendor(vendorId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_vendors')
      .delete()
      .eq('id', vendorId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting admin vendor:', error);
    throw error;
  }
}

export async function toggleVendorStatus(vendorId: string, isActive: boolean): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_vendors')
      .update({ is_active: isActive })
      .eq('id', vendorId);

    if (error) throw error;
  } catch (error) {
    console.error('Error toggling vendor status:', error);
    throw error;
  }
}

// Venue Management Functions
export async function getAdminVenues(): Promise<AdminVenue[]> {
  try {
    const { data, error } = await supabase
      .from('admin_venues')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching admin venues:', error);
    throw error;
  }
}

export async function createAdminVenue(venueData: VenueFormData): Promise<AdminVenue> {
  try {
    const { data, error } = await supabase
      .from('admin_venues')
      .insert([venueData])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating admin venue:', error);
    throw error;
  }
}

export async function updateAdminVenue(venueId: string, updates: Partial<VenueFormData>): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_venues')
      .update(updates)
      .eq('id', venueId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating admin venue:', error);
    throw error;
  }
}

export async function deleteAdminVenue(venueId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_venues')
      .delete()
      .eq('id', venueId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting admin venue:', error);
    throw error;
  }
}

export async function updateVenueStatus(venueId: string, status: 'active' | 'inactive' | 'maintenance'): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_venues')
      .update({ status, is_active: status === 'active' })
      .eq('id', venueId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating venue status:', error);
    throw error;
  }
}

// Public Data Functions (No auth required)
export async function getPublicVendors(): Promise<AdminVendor[]> {
  try {
    const { data, error } = await supabase
      .from('admin_vendors')
      .select('*')
      .eq('is_active', true)
      .order('rating', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching public vendors:', error);
    return [];
  }
}

export async function getPublicVenues(): Promise<AdminVenue[]> {
  try {
    const { data, error } = await supabase
      .from('admin_venues')
      .select('*')
      .eq('status', 'active')
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching public venues:', error);
    return [];
  }
}

// Import/Export Functions
export async function exportVendorData(): Promise<void> {
  try {
    const vendors = await getAdminVendors();
    
    const csvContent = [
      ['ID', 'Name', 'Contact Person', 'Email', 'Phone', 'Address', 'Website', 'Service Categories', 'Rating', 'Status', 'Created Date'].join(','),
      ...vendors.map(vendor => [
        vendor.id,
        `"${vendor.name}"`,
        `"${vendor.contact_person}"`,
        vendor.email,
        vendor.phone,
        `"${vendor.address}"`,
        vendor.website || '',
        `"${vendor.service_categories.join('; ')}"`,
        vendor.rating,
        vendor.is_active ? 'Active' : 'Inactive',
        new Date(vendor.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `admin_vendors_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting vendor data:', error);
    throw error;
  }
}

export async function exportVenueData(): Promise<void> {
  try {
    const venues = await getAdminVenues();
    
    const csvContent = [
      ['ID', 'Name', 'Address', 'Contact Email', 'Contact Phone', 'Max Capacity', 'Amenities', 'Status', 'Created Date'].join(','),
      ...venues.map(venue => [
        venue.id,
        `"${venue.name}"`,
        `"${venue.address}"`,
        venue.contact_email,
        venue.contact_phone,
        venue.capacity_details?.max_capacity || 0,
        `"${venue.amenities.join('; ')}"`,
        venue.status,
        new Date(venue.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `admin_venues_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting venue data:', error);
    throw error;
  }
}